/**
 * useUserProfile Hook
 * 
 * React hook for managing complete user profile data including preferences and activity.
 * Provides loading state, error handling, and preference synchronization.
 * 
 * @module useUserProfile
 */

import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { parseDate } from '../utils/dateUtils';
import userProfileService from '../services/userProfileService';
import { setPrefs } from '../redux/prefs/prefsSlice';

/**
 * Custom hook for managing user profile
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoFetch=true] - Whether to automatically fetch on mount
 * @param {boolean} [options.syncPreferences=true] - Sync preferences with Redux store
 * @returns {Object} Hook state and methods
 */
export const useUserProfile = ({
  autoFetch = true,
  syncPreferences = true
} = {}) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  const userId = user?.id;

  // Redux dispatch for preference updates
  const dispatch = useDispatch();

  /**
   * Fetch complete user profile from backend
   */
  const fetchProfile = useCallback(async (showLoading = true) => {
    if (!userId) {
      setProfile(null);
      return;
    }

    try {
      if (showLoading) setLoading(true);
      setError(null);

      const profileData = await userProfileService.getUserProfile(userId);
      
      if (profileData) {
        setProfile(profileData);
        setLastFetch(Date.now());

        // Sync preferences with Redux store if enabled
        if (syncPreferences && profileData.user?.prefs) {
          dispatch(setPrefs({
            lang: profileData.user.prefs.lang,
            cur: profileData.user.prefs.cur
          }));
        }
      }
    } catch (err) {
      console.error('useUserProfile: Failed to fetch profile:', err);
      setError(err.message || 'Failed to load user profile');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [userId, syncPreferences, dispatch]);

  /**
   * Update user activity
   * @param {string} activityType - Type of activity
   * @param {Object} metadata - Additional activity metadata
   * @returns {Promise<boolean>} True if successful
   */
  const updateActivity = useCallback(async (activityType, metadata = {}) => {
    if (!userId || !activityType) {
      return false;
    }

    try {
      const result = await userProfileService.updateUserActivity(userId, {
        activityType,
        metadata
      });

      if (result && profile) {
        // Update local profile with new activity
        setProfile(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            activity: {
              ...prev.profile.activity,
              ...result
            }
          }
        }));
      }

      return !!result;
    } catch (error) {
      console.error('useUserProfile: Failed to update activity:', error);
      return false;
    }
  }, [userId, profile]);

  /**
   * Track search activity
   * @param {Object} searchParams - Search parameters for tracking
   */
  const trackSearchActivity = useCallback(async (searchParams) => {
    return updateActivity('search', {
      fromLocation: searchParams.fromLocationCode,
      toLocation: searchParams.toLocationCode,
      duration: searchParams.endDate && searchParams.startDate 
        ? calculateDaysBetween(searchParams.startDate, searchParams.endDate)
        : null,
      age: searchParams.age
    });
  }, [updateActivity]);

  /**
   * Track page visit
   * @param {string} page - Page identifier
   * @param {Object} metadata - Additional page metadata
   */
  const trackVisit = useCallback(async (page, metadata = {}) => {
    return updateActivity('visit', {
      page,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }, [updateActivity]);

  /**
   * Calculate days between two date strings
   * @private
   */
  const calculateDaysBetween = (startDate, endDate) => {
    try {
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    } catch (error) {
      return null;
    }
  };

  /**
   * Refresh profile without showing loading state
   */
  const refreshProfile = useCallback(() => {
    fetchProfile(false);
  }, [fetchProfile]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Force refresh with loading state
   */
  const forceRefresh = useCallback(() => {
    fetchProfile(true);
  }, [fetchProfile]);

  // Auto-fetch on mount or when userId changes
  useEffect(() => {
    if (autoFetch && userId) {
      fetchProfile();
    }
  }, [autoFetch, userId, fetchProfile]);

  // Computed properties
  const recentSearches = profile?.profile?.recentSearches || [];
  const activity = profile?.profile?.activity || {};
  const achievements = profile?.profile?.achievements || [];
  const userInfo = profile?.user || {};

  return {
    profile,
    loading,
    error,
    lastFetch,
    
    // Profile data shortcuts
    recentSearches,
    activity,
    achievements,
    userInfo,
    
    // Activity stats
    totalSearches: activity.totalSearches || 0,
    totalBookings: activity.totalBookings || 0,
    lastSearch: activity.lastSearch,
    lastVisit: activity.lastVisit,
    
    // Methods
    fetchProfile,
    refreshProfile,
    forceRefresh,
    updateActivity,
    trackSearchActivity,
    trackVisit,
    clearError,
    
    // State checks
    hasProfile: !!profile,
    hasActivity: !!activity && Object.keys(activity).length > 0,
    hasSearchHistory: recentSearches.length > 0,
    isStale: lastFetch && (Date.now() - lastFetch) > 300000, // 5 minutes
    isEmpty: !profile && !loading
  };
};
